'use client';

import { usePathname } from 'next/navigation';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// 호버 이탈 시 메가메뉴 깜빡임 방지용 지연. trigger 와 panel 양쪽 onMouseLeave 가 동일 값 공유.
export const HOVER_CLOSE_DELAY_MS = 300;

type CloseMenuOptions = {
  delay?: number;
};

type MegaMenuContextValue = {
  activeMenuKey: string | undefined;
  openMenu: (key: string) => void;
  closeMenu: (opts?: CloseMenuOptions) => void;
  cancelDelayedClose: () => void;
  registerMenuRoot: (el: HTMLElement | null) => void;
  focusFirstPanelItem: () => void;
  focusLastPanelItem: () => void;
};

const MegaMenuContext = createContext<MegaMenuContextValue | null>(null);

/**
 * 메가메뉴는 마우스 전용 (트리거가 anchor + onMouseEnter). 키보드 접근은 햄버거 → site map.
 * 따라서 focus 복귀 / Esc 키 처리 없음 — 외부 클릭 + 라우트 변경만 닫힘 트리거.
 */
export const MegaMenuProvider = ({ children }: PropsWithChildren) => {
  const [activeMenuKey, setActiveMenuKey] = useState<string | undefined>();
  const menuRootRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pathname = usePathname();

  const cancelDelayedClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(
    (opts: CloseMenuOptions = {}) => {
      const { delay = 0 } = opts;
      cancelDelayedClose();
      if (delay > 0) {
        closeTimeoutRef.current = setTimeout(() => {
          setActiveMenuKey(undefined);
          closeTimeoutRef.current = null;
        }, delay);
      } else {
        setActiveMenuKey(undefined);
      }
    },
    [cancelDelayedClose],
  );

  const openMenu = useCallback(
    (key: string) => {
      cancelDelayedClose();
      setActiveMenuKey(key);
    },
    [cancelDelayedClose],
  );

  const registerMenuRoot = useCallback((el: HTMLElement | null) => {
    menuRootRef.current = el;
  }, []);

  // 키보드 trigger 에서 ArrowDown / ArrowUp 시 panel 첫/마지막 anchor 로 focus 이동.
  const focusFirstPanelItem = useCallback(() => {
    menuRootRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
  }, []);
  const focusLastPanelItem = useCallback(() => {
    const anchors =
      menuRootRef.current?.querySelectorAll<HTMLAnchorElement>('a');
    if (!anchors || anchors.length === 0) return;
    anchors[anchors.length - 1].focus();
  }, []);

  useEffect(
    function closeMenuOnRouteChange() {
      closeMenu();
    },
    [pathname, closeMenu],
  );

  useEffect(
    function attachOutsideClickListener() {
      if (activeMenuKey === undefined) return;
      function handlePointerDown(e: PointerEvent) {
        const target = e.target as Node | null;
        if (target && menuRootRef.current?.contains(target)) return;
        setActiveMenuKey(undefined);
      }
      document.addEventListener('pointerdown', handlePointerDown);
      return function detachOutsideClickListener() {
        document.removeEventListener('pointerdown', handlePointerDown);
      };
    },
    [activeMenuKey],
  );

  // unmount 시 pending close timeout 정리 — 라우트 전환 등으로 Provider 가 사라져도 leak 없음.
  useEffect(
    () =>
      function clearPendingCloseOnUnmount() {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      },
    [],
  );

  // Provider value 객체 안정화 — consumer 가 activeMenuKey 변경 시에만 재렌더되도록.
  const value = useMemo<MegaMenuContextValue>(
    () => ({
      activeMenuKey,
      openMenu,
      closeMenu,
      cancelDelayedClose,
      registerMenuRoot,
      focusFirstPanelItem,
      focusLastPanelItem,
    }),
    [
      activeMenuKey,
      openMenu,
      closeMenu,
      cancelDelayedClose,
      registerMenuRoot,
      focusFirstPanelItem,
      focusLastPanelItem,
    ],
  );

  return (
    <MegaMenuContext.Provider value={value}>
      {children}
    </MegaMenuContext.Provider>
  );
};

export const useMegaMenu = () => {
  const context = useContext(MegaMenuContext);
  if (!context)
    throw new Error('useMegaMenu는 MegaMenuProvider에 속해야 합니다.');
  return context;
};
